import { FolderSearch, ShieldCheck, Users } from 'lucide-react';
import { santoroTestimonial } from '@/content/testimonials';
import type { SolutionContent } from './types';

export const studiLegaliSolution: SolutionContent = {
  slug: 'studi-legali',
  navLabel: 'Studi legali',
  navDescription: 'Titolari, collaboratori e praticanti sullo stesso standard.',
  meta: {
    title: 'AI per studi legali',
    description:
      'Un solo abbonamento per tutto lo studio: titolari, collaboratori e praticanti producono atti con lo stesso standard, con norma e giurisprudenza sempre citate e verificabili.',
  },
  hero: {
    title: 'Trovanorma per studi legali',
    subtitle:
      'Al tuo fianco quando ricerca, analisi, redazione e revisione fanno parte del lavoro di più professionisti, anche su casi diversi.',
  },
  showcaseAudience: 'avvocati',
  queryExamples: [
    {
      title: 'Ricerca di giurisprudenza',
      icon: 'BookOpen',
      question:
        'Cerca la giurisprudenza recente sulla responsabilità della banca per operazioni non autorizzate e sintetizzami i principi di diritto.',
      result: 'Sintesi con citazioni',
    },
    {
      title: 'Revisione di un contratto',
      icon: 'FileSearch',
      question:
        'Allego il contratto di finanziamento della società assistita: estrai le clausole vincolanti e proponi le modifiche a tutela nel recesso anticipato.',
      attachment: 'contratto-finanziamento.pdf',
      result: 'Revisione puntuale',
    },
    {
      title: 'Confronto fra bozze',
      icon: 'GitCompareArrows',
      question:
        'Confronta la bozza che abbiamo inviato con quella tornata dal legale avverso: elencami ogni modifica e il suo effetto.',
      attachment: 'bozze-a-confronto.docx',
      result: 'Differenze riga per riga',
    },
    {
      title: 'Rito semplificato o citazione?',
      icon: 'Scale',
      question:
        'Quando conviene introdurre la causa col rito semplificato invece che con la citazione? Mettimi termini e preclusioni a confronto in una tabella.',
      result: 'Tabella comparativa',
    },
    {
      title: 'Procura e deleghe',
      icon: 'FileSignature',
      question:
        'Predisponi la procura alle liti per il giudizio in allegato e la delega per l’udienza al collega di studio.',
      attachment: 'atto-di-citazione.pdf',
      result: 'Modelli pronti',
    },
    {
      title: 'Nota spese di giudizio',
      icon: 'Calculator',
      question:
        'Prepara la nota spese secondo i parametri DM 55/2014: valore **80.000 €**, tutte le fasi, davanti al Tribunale.',
      result: 'Foglio di calcolo',
    },
  ],
  tasksTitle: 'Più di una semplice risposta, per tutto il lavoro dello studio',
  tasks: [
    {
      icon: ShieldCheck,
      title: 'Lo stesso standard, da chiunque parta il lavoro',
      description:
        'Fonti e riferimenti restano disponibili per la verifica, così chi rivede il lavoro può concentrarsi sull’argomentazione invece di ricostruire da capo la ricerca.',
    },
    {
      icon: FolderSearch,
      title: 'Il lavoro fatto non si perde',
      description:
        'Ricerche, conversazioni e documenti condivisi restano nell’area di lavoro e possono essere ritrovati dai colleghi quando lo stesso tema torna su un altro fascicolo.',
    },
    {
      icon: Users,
      title: 'Collaborazione senza account condivisi',
      description:
        'Ogni professionista entra con il proprio accesso. Lo studio gestisce utenti e ruoli e decide quali conversazioni lasciare private, condividere con alcuni colleghi o rendere disponibili al team.',
    },
  ],
  comparison: {
    title: 'Non tutte le AI sono costruite per il lavoro di uno studio',
    text: 'In uno studio una risposta deve poter essere verificata, condivisa e ripresa da chi continua il lavoro.',
    genericLabel: 'AI generalista',
    drawbacks: [
      'Riferimenti a norme e sentenze non sempre presenti',
      'Aggiornamento delle fonti non garantito',
      'Conversazioni e materiali distribuiti tra account',
      'Contesto difficile da riprendere da altri professionisti',
      'Visibilità e ruoli non gestiti a livello di studio',
    ],
    strengths: [
      'Riferimenti collegati ai documenti originali',
      'Fonti ufficiali sincronizzate ogni giorno',
      'Conversazioni condivisibili quando serve',
      'Storico e materiali recuperabili dal team',
      'Utenti, ruoli e visibilità gestiti nell’area di lavoro',
    ],
  },
  featuresTitle: 'Le funzionalità più utili nel lavoro legale',
  featuredFeatures: [
    'ricerca-e-quesiti',
    'analisi-documentale',
    'redazione-documenti',
    {
      slug: 'revisione-confronto',
      description:
        'Porta una bozza già lavorata, ricevi una versione rivista e continua a migliorarla insieme al team.',
    },
    {
      slug: 'collaborazione',
      description:
        'Condividi conversazioni e materiali con i colleghi, mantenendo il controllo su accessi e visibilità.',
    },
  ],
  cta: {
    title: 'Porta Trovanorma nel lavoro del tuo studio',
    text: 'Provalo su un caso reale e scopri come può supportare ricerca, analisi, redazione e revisione, mantenendo il lavoro condivisibile e riutilizzabile dal team.',
  },
  testimonial: santoroTestimonial,
};
